<template>
  <div class="about-eyuan-page">
    <section class="hero">
      <div class="hero-inner">
        <p class="eyebrow">{{ pageCopy.hero.eyebrow }}</p>
        <h1>{{ pageCopy.hero.title }}</h1>
        <p class="hero-lead">
          {{ pageCopy.hero.lead }}
        </p>
      </div>
    </section>

    <section class="content-section">
      <div class="content-grid">
        <article v-for="card in pageCopy.cards" :key="card.id" class="content-card">
          <h2>{{ card.title }}</h2>
          <p v-if="card.body">
            {{ card.body }}
          </p>
          <ul v-else-if="card.items">
            <li v-for="(item, idx) in card.items" :key="idx">
              {{ item }}
            </li>
          </ul>
        </article>
      </div>
    </section>

    <section class="contact-section">
      <div class="contact-card">
        <h3>{{ pageCopy.contact.title }}</h3>
        <p>{{ pageCopy.contact.description }}</p>
        <a class="contact-email" href="mailto:support@eyuanquant.com">{{ pageCopy.contact.emailLabel }}</a>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { aboutEyuanContent } from '@/content/aboutEyuan';

const { locale } = useI18n();
const pageCopy = computed(
  () => aboutEyuanContent[locale.value] ?? aboutEyuanContent['en-US']
);
</script>

<style scoped>
.about-eyuan-page {
  display: flex;
  flex-direction: column;
  gap: 4rem;
  padding-bottom: 4rem;
  color: var(--color-text-primary, #0f172a);
  background: linear-gradient(180deg, #f4f6fb 0%, #ffffff 55%, #ffffff 100%);
}

.hero {
  padding: 5rem 1.5rem 3rem;
  background: radial-gradient(circle at top right, rgba(59, 130, 246, 0.12), transparent 45%);
}

.hero-inner {
  max-width: 920px;
  margin: 0 auto;
  text-align: center;
}

.eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 1rem;
  margin-bottom: 1.25rem;
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 999px;
  font-size: 0.85rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #2563eb;
  background-color: rgba(59, 130, 246, 0.08);
  font-weight: 600;
}

.hero h1 {
  font-size: clamp(2.5rem, 4vw, 3.5rem);
  letter-spacing: -0.04em;
  margin-bottom: 1.5rem;
}

.hero-lead {
  font-size: clamp(1.1rem, 2vw, 1.4rem);
  line-height: 1.7;
  color: var(--color-text-secondary, #475569);
}

.content-section {
  padding: 0 1.5rem;
}

.content-grid {
  max-width: 1080px;
  margin: 0 auto;
  display: grid;
  gap: 1.75rem;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}

.content-card {
  background: white;
  border-radius: 20px;
  padding: 2.2rem;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.08);
  border: 1px solid rgba(148, 163, 184, 0.18);
  transition: transform 0.24s ease, box-shadow 0.24s ease;
}

.content-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 32px 60px rgba(15, 23, 42, 0.12);
}

.content-card h2 {
  font-size: 1.6rem;
  margin-bottom: 1.25rem;
}

.content-card p {
  line-height: 1.7;
  color: var(--color-text-secondary, #475569);
}

.content-card ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  color: var(--color-text-secondary, #475569);
}

.content-card li::before {
  content: '•';
  color: #2563eb;
  font-weight: 700;
  display: inline-block;
  width: 1.1rem;
}

.contact-section {
  padding: 0 1.5rem;
}

.contact-card {
  max-width: 720px;
  margin: 0 auto;
  padding: 2.5rem 2rem;
  border-radius: 24px;
  text-align: center;
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.9), rgba(109, 40, 217, 0.9));
  color: white;
  box-shadow: 0 28px 52px rgba(59, 130, 246, 0.25);
}

.contact-card h3 {
  font-size: 1.9rem;
  margin-bottom: 1rem;
}

.contact-card p {
  font-size: 1.1rem;
  line-height: 1.65;
  margin-bottom: 1.5rem;
  color: rgba(255, 255, 255, 0.92);
}

.contact-email {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 0.85rem 1.8rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 1rem;
  color: #1d4ed8;
  background: white;
  text-decoration: none;
  transition: background 0.24s ease, color 0.24s ease, transform 0.24s ease;
}

.contact-email:hover {
  color: white;
  background: rgba(255, 255, 255, 0.12);
  transform: translateY(-2px);
}

@media (max-width: 600px) {
  .hero {
    padding: 3.5rem 1rem 2.5rem;
  }

  .content-card {
    padding: 1.75rem;
  }

  .contact-card {
    padding: 2rem 1.5rem;
  }
}
</style>
